import { useCallback, useEffect, useMemo } from "react";
import {
  useInstagramConversations,
  instagramConversations$,
} from "@jugl-web/rest-api/integrations/hooks/useInstagramConversations";
import { InstagramConversationModel } from "@jugl-web/rest-api/integrations/models/InstagramConversation";
import { DataLoadingWrapper } from "@jugl-web/ui-components";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useInView } from "react-intersection-observer";
import {
  CRMChannel,
  CRMConversationFilter,
  CRMConversationMode,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { InstagramConversationTile } from "../InstagramConversationTile";
import { ConversationListEmptyState } from "../ConversationListEmptyState";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useSelectedConversation } from "../../hooks/useSelectedConversation";
import { reactivateClosedConversation } from "../../../../utils/reactivateClosedConversation";

export interface InstagramConversationsProps {
  searchQuery: string;
  selectedFilter: CRMConversationFilter;
  setSelectedFilter: (value: CRMConversationFilter) => void;
  selectedMode: CRMConversationMode;
}

export const InstagramConversations: React.FC<InstagramConversationsProps> = ({
  selectedMode,
  searchQuery,
  selectedFilter,
  setSelectedFilter,
}) => {
  const {
    selectedConversation,
    selectConversation,
    incomingInstagramMessages$,
  } = useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const [fetchConversationById] =
    integrationsApi.useLazyGetInstagramConversationByIdQuery();
  const { ref: inViewRef, inView } = useInView();
  const { me } = useMe();

  // Compute filter for Instagram based on selected mode
  const computedFilter = useMemo(() => {
    if (selectedFilter !== CRMConversationFilter.active) {
      return selectedFilter;
    }

    switch (selectedMode) {
      case CRMConversationMode.managedByMe:
        return me?.id || "";
      case CRMConversationMode.waitingResponse:
      case CRMConversationMode.onAIAutoReply:
        return selectedMode;
      default:
        return CRMConversationFilter.active;
    }
  }, [selectedFilter, selectedMode, me?.id]);

  const {
    data: conversations,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    retry,
    loadMore,
  } = useInstagramConversations({
    entityId,
    searchTerm: searchQuery,
    filter: computedFilter,
    pageSize: 10,
  });

  useSelectedConversation({
    channel: CRMChannel.instagram,
    selectedFilter,
    setSelectedFilter,
  });

  const prependConversationById = useCallback(
    async (convId: string) => {
      const response = await fetchConversationById({ entityId, convId });

      if (response && "data" in response && response.data) {
        instagramConversations$.next({
          type: "prepend",
          conversation: response.data,
        });
      }
    },
    [entityId, fetchConversationById]
  );

  // Handle real-time message updates
  useEffect(() => {
    const subscription = incomingInstagramMessages$.subscribe((message) => {
      const conversationExists = conversations.some(
        (c) => c.conv_id === message.conv_id
      );

      if (!conversationExists) {
        prependConversationById(message.conv_id);
        return;
      }

      // Update selected conversation if it matches
      if (selectedConversation?.conv_id === message.conv_id) {
        if (
          (selectedConversation.msg_id === message.msg_id &&
            (selectedConversation as InstagramConversationModel).inserted_at >=
              message.inserted_at) ||
          message.msg_status === "new"
        ) {
          const updatedConversation = reactivateClosedConversation({
            ...selectedConversation,
            ...message,
          });
          instagramConversations$.next({
            type: "update",
            conversation: updatedConversation as InstagramConversationModel,
          });
          selectConversation(
            updatedConversation as InstagramConversationModel,
            true
          );
        }
      }

      // Find existing conversation and update it
      const existingConversation = conversations.find(
        (c) => c.conv_id === message.conv_id
      );

      if (
        existingConversation &&
        existingConversation.inserted_at <= message.inserted_at &&
        message.msg_status === "new"
      ) {
        const updatedConversation: InstagramConversationModel =
          reactivateClosedConversation({
            ...existingConversation,
            ...message,
          } as InstagramConversationModel);

        if (message.type === "incoming") {
          updatedConversation.since = message.inserted_at;
        }
        // Prepend conversation to bump it to the top
        instagramConversations$.next({
          type: "prepend",
          conversation: updatedConversation,
        });

        // Also update selected conversation if needed
        if (selectedConversation?.conv_id === updatedConversation.conv_id) {
          selectConversation(updatedConversation, true);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [
    incomingInstagramMessages$,
    conversations,
    prependConversationById,
    selectedConversation,
    selectConversation,
  ]);

  // Handle infinite scroll
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [inView, hasMore, isLoadingMore, loadMore]);

  return (
    <DataLoadingWrapper
      isError={isError}
      isLoading={isLoading}
      onRetry={retry}
      isLoadingMore={isLoadingMore}
      isLoadingMoreError={isLoadingMoreError}
    >
      {conversations.map((conversation) => (
        <InstagramConversationTile
          key={conversation.conv_id}
          conversation={conversation}
          searchQuery={searchQuery}
          isDraftUnavailable={selectedFilter === CRMConversationFilter.expired}
        />
      ))}
      {hasMore && !isLoading && !isLoadingMore && <div ref={inViewRef} />}
      <ConversationListEmptyState
        hasConversations={conversations.length > 0}
        searchQuery={searchQuery}
        selectedFilter={selectedFilter}
        selectedMode={selectedMode}
      />
    </DataLoadingWrapper>
  );
};
